import submitCareerForm from "@crescentParts/Handlers/Custom/CareerHandler";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    submitCareerForm,
    loadCurrentCountries,
};

const mapStateToProps = (state: ApplicationState) => ({
    countries: getCurrentCountries(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CareerFormStyles {
    submitErrorGridItem: GridItemProps;
    submitErrorTitle: TypographyProps;
    submitGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    container?: InjectableCss;
    headcontainer?: InjectableCss;
    formTextField?: TextFieldPresentationProps;
    messageTextArea?: TextAreaProps;
    formFieldGridItem?: GridItemProps;
    formFieldParallelGridItem?: GridItemProps;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    fileUploader?: FileUploadPresentationProps;
    label?: InjectableCss;
    stateSelect?: InjectableCss;
}

export const contactFormStyles: CareerFormStyles = {
    headcontainer: {
        css: css`
            box-sizing: border-box;
            width: 100%;
            background: rgba(225, 221, 226, 1);
            border: 1px solid #e1dde2;
            border-radius: 8px 8px 0px 0px;
        `,
    },
    container: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            padding: 15px 30px 28px 30px;
            border-radius: 0px 0px 8px 8px;
            background: rgba(225, 221, 226, 0.3);
            margin-bottom: 20px;
            justify-content: space-evenly;
            [class^="FormFieldStyle"] {
                order: 1;
                justify-content: center;
                display: flex;
            }
            [class^="FormInputWrapper"] {
                margin-top: 4px !important;
            }
            [class^="UploadWrapper"] {
                margin-bottom: 11px;
            }
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fileUploader: {
        cssOverrides: {
            formInputWrapper: css`
                margin-left: 10px;
                > [class^="FileUploadStyle"] {
                    &:focus {
                        border: none !important;
                    }
                }
            `,
            inputSelect: css`
                background: transparent;
                border: none;
                padding: 0 10px;
                border-radius: 8px !important;
                &:focus {
                    border: none !important;
                }
            `,
        },
        clearIconProps: {
            css: css`
                display: none;
            `,
        },
        iconProps: {
            css: css`
                display: none;
            `,
        },
        buttonProps: {
            css: css`
                margin: 4px 0 0 0;
                border: 1px solid #cdcccd;
                min-height: auto;
                font-size: 14px !important;
            `,
            color: "#EEEDED",
        },
    },
    formTextField: {
        css: css`
            border-radius: 8px !important;
            padding: 0px;
        `,
    },
    messageTextArea: {
        css: css`
            border-radius: 8px !important;
        `,
    },

    formFieldGridItem: {
        width: 12,
        css: css`
            padding: 10px 0;
        `,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    submitGridItem: {
        width: 12,
        css: css`
            padding: 40px 0 10px 0;
        `,
    },
    formFieldParallelGridItem: {
        width: 6,
    },
    label: {
        css: css`
            color: #0e0e0e;
            font-size: 15px;
            font-weight: 600;
            margin-top: 10px;
        `,
    },
    stateSelect: {
        css: css`
            border-radius: 8px !important;
            padding: 0px;
        `,
    },
};

const styles = contactFormStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

export const CareerForm: FC<Props> = ({ id, submitCareerForm, loadCurrentCountries, countries }) => {
    const firstNameRequiredFieldMessage = siteMessage("First Name is required");
    const lastNameRequiredFieldMessage = siteMessage("Last Name is required");
    const addressRequiredFieldMessage = siteMessage("Address is required");
    const cityRequiredFieldMessage = siteMessage("City is required");
    const stateRequiredFieldMessage = siteMessage("State is required");
    const zipRequiredFieldMessage = siteMessage("ZIP is required");
    const phoneRequiredfieldMessage = siteMessage("Phone No. is required");
    const emailRequiredFieldMessage = siteMessage("Email is Required");
    const positionDesiredFieldMessage = siteMessage("Position Desired is required");
    const hearAboutUsFieldMessage = siteMessage("This field is required");
    const resumeErrorFieldMessage = siteMessage("Resume is required");
    const validPhoneMsg = siteMessage("Enter a Valid Phone Number");

    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState(firstNameRequiredFieldMessage);
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState(lastNameRequiredFieldMessage);
    const [address, setAddress] = useState("");
    const [addressError, setAddressError] = useState(addressRequiredFieldMessage);
    const [addressLine2, setAddressLine2] = useState("");
    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState(cityRequiredFieldMessage);
    const [state, setState] = useState("");
    const [stateError, setStateError] = useState(stateRequiredFieldMessage);
    const [zip, setZip] = useState("");
    const [zipError, setZipError] = useState(zipRequiredFieldMessage);
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState(phoneRequiredfieldMessage);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [positionDesired, setPositionDesired] = useState("");
    const [positionDesiredError, setPositionDesiredError] = useState(positionDesiredFieldMessage);
    const [hearAboutUs, setHearAboutUs] = useState("");
    const [hearAboutUsError, setHearAboutUsError] = useState(hearAboutUsFieldMessage);
    const [resume, setResume] = useState<any>(null);
    const [resumeError, setResumeError] = useState(resumeErrorFieldMessage);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const fileUploadPartitionerLicenceRef = useRef({ value: "" } as HTMLInputElement);
    const [allStates, setAllStates] = useState<any>();

    React.useEffect(() => {
        loadCurrentCountries();
    }, []);

    React.useEffect(() => {
        if (countries) {
            const getStates = countries?.find((option: any) => option.name === "United States");
            setAllStates(getStates?.states);
        }
    }, [countries]);

    const validateFirstName = (name: string) => {
        const errorMessage = !name ? firstNameRequiredFieldMessage : "";
        setFirstNameError(errorMessage);
        return !errorMessage;
    };
    const validateLastName = (value: string) => {
        const errorMessage = !value ? lastNameRequiredFieldMessage : "";
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validateAddress = (value: string) => {
        const errorMessage = !value ? addressRequiredFieldMessage : "";
        setAddressError(errorMessage);
        return !errorMessage;
    };

    const validateCity = (value: string) => {
        const errorMessage = !value ? cityRequiredFieldMessage : "";
        setCityError(errorMessage);
        return !errorMessage;
    };

    const validateState = (value: string) => {
        const errorMessage = !value ? stateRequiredFieldMessage : "";
        setStateError(errorMessage);
        return !errorMessage;
    };

    const validateZip = (value: string) => {
        const errorMessage = !value ? zipRequiredFieldMessage : "";
        setZipError(errorMessage);
        return !errorMessage;
    };

    const addDashToPhone = (value: string) => {
        if (value.length > 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        }
        if (value.length > 3 && value.length <= 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}`;
        }
        return value;
    };

    const removeDashFromPhone = (value: string) => {
        return value.replace(/\D/g, "");
    };

    const phoneHandler = (value: string) => {
        const regex = /[0-9]|\./;
        if (!value) {
            setPhone("");
            validatePhone(value);
        }
        value = removeDashFromPhone(value);
        if (!regex.test(value) || value.length > 10) {
            return;
        }
        if (value) {
            const formattedPhone = addDashToPhone(value);
            formattedPhone !== undefined && setPhone(formattedPhone);
            validatePhone(formattedPhone);
        }
    };

    const validatePhone = (value: string) => {
        value = removeDashFromPhone(value);
        const errorMessage = !value
            ? phoneRequiredfieldMessage
            : Number(value) && value.length === 10
            ? ""
            : validPhoneMsg;
        setPhoneError(errorMessage);
        return !errorMessage;
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : "Email is not valid";
        setEmailError(errorMessage);
        return !errorMessage;
    };
    const validatePositionDesired = (value: string) => {
        const errorMessage = !value ? positionDesiredFieldMessage : "";
        setPositionDesiredError(errorMessage);
        return !errorMessage;
    };
    const validateHearAboutUs = (value: string) => {
        const errorMessage = !value ? hearAboutUsFieldMessage : "";
        setHearAboutUsError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(
            firstNameError ||
            lastNameError ||
            addressError ||
            cityError ||
            stateError ||
            zipError ||
            phoneError ||
            emailError ||
            positionDesiredError ||
            hearAboutUsError ||
            resumeError ||
            incorrectFileExtension
        );
    };

    const firstNameChangeHandler = (value: string) => {
        validateFirstName(value);
        setFirstName(value);
    };
    const lastNameChangeHandler = (value: string) => {
        validateLastName(value);
        setLastName(value);
    };
    const addressChangeHandler = (value: string) => {
        validateAddress(value);
        setAddress(value);
    };
    const addrLine2ChangeHandler = (value: string) => {
        setAddressLine2(value);
    };
    const cityChangeHandler = (value: string) => {
        validateCity(value);
        setCity(value);
    };
    const stateChangeHandler = (value: string) => {
        validateState(value);
        setState(value);
    };
    const zipChangeHandler = (value: string) => {
        validateZip(value);
        setZip(value);
    };
    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };
    const positionDesiredChangeHandler = (value: string) => {
        validatePositionDesired(value);
        setPositionDesired(value);
    };
    const hearAboutUsChangeHandler = (value: string) => {
        validateHearAboutUs(value);
        setHearAboutUs(value);
    };
    const resumeChangeHandler = (event: React.ChangeEvent<any>) => {
        event.stopPropagation();
        event.preventDefault();
        const uploadedFile = event.target.files[0];
        setResume(uploadedFile);
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectExtension = ["doc", "docx", "pdf"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectExtension);
            setIsBadFile({ isBadFile: incorrectExtension });
            setUploadLimitExceeded({ uploadLimitExceeded: false });
            setResumeError("");
        } else {
            setResumeError(resumeErrorFieldMessage);
        }
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Successfully submitted!"), messageType: "success" });
        clearFormFields();
    };

    const clearFormFields = () => {
        setFirstName("");
        setLastName("");
        setAddress("");
        setAddressLine2("");
        setCity("");
        setState("");
        setZip("");
        setPhone("");
        setEmail("");
        setPositionDesired("");
        setHearAboutUs("");
        setResume("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setFirstNameError(firstNameRequiredFieldMessage);
        setLastNameError(lastNameRequiredFieldMessage);
        setAddressError(addressRequiredFieldMessage);
        setCityError(cityRequiredFieldMessage);
        setStateError(stateRequiredFieldMessage);
        setZipError(zipRequiredFieldMessage);
        setPhoneError(phoneRequiredfieldMessage);
        setEmailError(emailRequiredFieldMessage);
        setPositionDesiredError(positionDesiredFieldMessage);
        setHearAboutUsError(hearAboutUsFieldMessage);
        setResumeError(resumeErrorFieldMessage);
    };
    // eslint-disable-next-line require-await
    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);

        submitCareerForm({
            FirstName: firstName,
            LastName: lastName,
            Address1: address,
            Address2: addressLine2,
            City: city,
            State: state,
            ZIP: zip,
            Phone: phone,
            Email: email,
            PositionDesired: positionDesired,
            HearAboutUs: hearAboutUs,
            Resume: resume,
            onSuccess: onSuccessHandler,
            onError: error => {
                if (error) {
                    setErrorMessage(error);
                    setIsSubmitting(false);
                    const errorMsg = error.toString().length > 400 ? "Something went wrong!" : error;
                    toasterContext.addToast({ body: translate(errorMsg), messageType: "danger" });
                } else {
                    toasterContext.addToast({ body: translate("Something went wrong!"), messageType: "danger" });
                }
            },
        });
    };

    const isSubmitEnabled = validateSubmitEnabled();
    return (
        <>
            <StyledWrapper {...styles.headcontainer}>
                <Zone zoneName="Heading" contentId={id}></Zone>
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <StyledForm onSubmit={submitHandler} noValidate>
                    <GridContainer>
                        <GridItem {...styles.formFieldParallelGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("First Name")}
                                onChange={e => firstNameChangeHandler(e.currentTarget.value)}
                                value={firstName}
                                data-test-selector="career_firstname"
                                required
                                error={isSubmitted && firstNameError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldParallelGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("Last Name")}
                                onChange={e => lastNameChangeHandler(e.currentTarget.value)}
                                value={lastName}
                                data-test-selector="career_lastname"
                                required
                                error={isSubmitted && lastNameError}
                            />
                        </GridItem>
                    </GridContainer>
                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label={translate("Address")}
                            onChange={e => addressChangeHandler(e.currentTarget.value)}
                            value={address}
                            data-test-selector="career_address"
                            required
                            error={isSubmitted && addressError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label={translate("Address Line 2")}
                            onChange={e => addrLine2ChangeHandler(e.currentTarget.value)}
                            value={addressLine2}
                            data-test-selector="career_address1"
                        />
                    </GridItem>

                    <GridContainer>
                        <GridItem {...styles.formFieldParallelGridItem}>
                            <TextField
                                {...styles.formTextField}
                                type="text"
                                label={translate("City")}
                                onChange={e => cityChangeHandler(e.currentTarget.value)}
                                value={city}
                                required
                                data-test-selector="career_city"
                                error={isSubmitted && cityError}
                            />
                        </GridItem>

                        <GridItem {...styles.formFieldParallelGridItem}>
                            <Select
                                {...styles.stateSelect}
                                label={translate("State")}
                                data-test-selector="createAccount_state"
                                placeholder="Select State"
                                value={state}
                                required
                                error={isSubmitted && stateError}
                                onChange={e => stateChangeHandler(e.currentTarget.value)}
                            >
                                <option value="">Select State</option>
                                {allStates?.map((state: any, index: number) => {
                                    return (
                                        <option value={state.name} key={index}>
                                            {state.name}
                                        </option>
                                    );
                                })}
                            </Select>
                        </GridItem>
                    </GridContainer>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label="ZIP Code"
                            onChange={e => zipChangeHandler(e.currentTarget.value)}
                            value={zip}
                            required
                            data-test-selector="career_zip"
                            error={isSubmitted && zipError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label={translate("Phone")}
                            onChange={e => phoneHandler(e.currentTarget.value)}
                            value={phone}
                            data-test-selector="career_phone"
                            required
                            error={isSubmitted && phoneError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label={translate("Email")}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            value={email}
                            data-test-selector="career_email"
                            required
                            error={isSubmitted && emailError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextField
                            {...styles.formTextField}
                            type="text"
                            label={translate("Position Desired")}
                            onChange={e => positionDesiredChangeHandler(e.currentTarget.value)}
                            value={positionDesired}
                            data-test-selector="career_positionDesired"
                            required
                            error={isSubmitted && positionDesiredError}
                        />
                    </GridItem>

                    <GridItem {...styles.formFieldGridItem}>
                        <TextArea
                            {...styles.messageTextArea}
                            // type="text"
                            label={translate("How did you hear about us?")}
                            onChange={e => hearAboutUsChangeHandler(e.currentTarget.value)}
                            value={hearAboutUs}
                            data-test-selector="career_HearAboutUs"
                            required
                            error={isSubmitted && hearAboutUsError}
                            rows={2}
                        />
                    </GridItem>

                    <StyledWrapper {...styles.label}>Upload your Resume*</StyledWrapper>

                    <FileUpload
                        {...styles.fileUploader}
                        name="partitionerCertificate"
                        accept=".pdf,.jpg,.png"
                        fileName={resume?.name || "No File Chosen"}
                        inputRef={fileUploadPartitionerLicenceRef}
                        error={
                            incorrectFileExtension
                                ? translate("must be .doc, .docx or .pdf")
                                : isSubmitted && !resume && resumeError
                        }
                        onFileChange={resumeChangeHandler}
                        required
                    />

                    <StyledWrapper>Accepted file types: doc, docx, pdf, Max. file size: 50 MB.</StyledWrapper>

                    <GridItem {...styles.submitGridItem}>
                        <Button
                            {...styles.submitButton}
                            type="submit"
                            disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                            data-test-selector="createAccount_createButton"
                        >
                            {translate("Submit")}
                        </Button>
                    </GridItem>
                </StyledForm>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CareerForm),
    definition: {
        group: "Common",
        displayName: "Career Form",
    },
};

export default widgetModule;
